import {Device} from '@fantasmagames/core/src/device/Device';
import {CoreDI} from '@fantasmagames/core/src/di/CoreDI';
import {IEventManagerWithAsync} from '@fantasmagames/core/src/events/_interfaces/IEventManager';
import {ScreenEvents} from '@fantasmagames/core/src/screen/ScreenEvents';
import {System} from '@fantasmagames/core/src/System';
import {UpdateManager} from '@fantasmagames/core/src/update/UpdateManager';
import DI from '@fantasmagames/slot/src/common/inversify/constants/DI';
import {StatedDebug} from '@fantasmagames/slot/src/common/inversify/debug/StatedDebug';
import {ModuleState} from '@fantasmagames/slot/src/common/ModuleState';
import {ISettings} from '@fantasmagames/slot/src/common/settings/_interfaces/ISettings';
import {View} from '@fantasmagames/slot/src/common/view/View';
import {ViewManager} from '@fantasmagames/slot/src/common/view/ViewManager';
import {IModule} from '@fantasmagames/slot/src/common/_interfaces/IModule';
import {Logic} from '@fantasmagames/slot/src/logic/Logic';
import {SoundPlayer} from '@fantasmagames/slot/src/sound/SoundPlayer';
import {inject, injectable} from 'inversify';
import 'reflect-metadata';
import {TimerUtils} from '@fantasmagames/core/src/update/TimerUtils';

export interface ModuleStatesMap<V extends View, M extends Module<V>> {
	[name: string]: ModuleState<V, M>;
}

/**
 * an abstract class for every game module to be extended from
 * handles views and module-to-module events.
 * also changes the states of current module and handles events from view
 */

@injectable()
export abstract class Module<V extends View> implements IModule {
	private static _debugInfo: StatedDebug = new StatedDebug();

	@inject(DI.ViewManager)
	protected _viewManager: ViewManager;

	@inject(CoreDI.IEventManagerWithAsync)
	protected _eventManager: IEventManagerWithAsync;

	@inject(DI.ISettings)
	protected _settings: ISettings;

	@inject(CoreDI.System)
	protected _system: System;

	@inject(CoreDI.UpdateManager)
	protected _updateManager: UpdateManager;

	@inject(DI.Logic)
	protected _logic: Logic;

	@inject(DI.SoundPlayer)
	protected _soundPlayer: SoundPlayer;

	@inject(CoreDI.Device)
	protected _device: Device;

	protected _view: V;

	protected _states: ModuleStatesMap<V, Module<V>>;

	protected _currentState: ModuleState<V, Module<V>>;
	protected _previousStateName: string;

	protected _moduleStateHandlingEvents: string[] = [];

	private _timeUtils: TimerUtils;

	public init(): void {
		this._states = {};

		this._timeUtils = new TimerUtils(this.updateManager);

		this.setupViews();
		this.setupStates();
		this.setupEvents();
		this.enterFirstState();

		if (!__PRODUCTION__) {
			if (!(document as any).MODULES) {
				(document as any).MODULES = {};
			}
			(document as any).MODULES[this.constructor.name] = this;
		}
	}

	public afterInit(): void {}

	public addDebuggingComponent(): void {}

	public restore(): void {}

	public gotoState(name: string): void {
		this.changeStateTo(name);
	}

	public gotoPreviousState(): void {
		if (this._previousStateName && this._previousStateName.length > 0) {
			const fromState: string = this._currentState ? this._currentState.name : '';
			this._currentState && this._currentState.onLeaveState();
			this._currentState = this._states[this._previousStateName];
			this._previousStateName = fromState;

			if (!__PRODUCTION__) {
				Module._debugInfo.setModuleState(this.constructor.name, this._currentState.name);
				console.log('' + this.constructor.name + ': ' + fromState + ' -> ' + this.currentState.name);
			}
		} else {
			throw new Error('Module::gotoPreviousState previous state does not exist');
		}
	}

	protected setupEvents(): void {
		if (this._view) {
			this.eventManager.on(ScreenEvents.LANDSCAPE_MODE, this._view.onLandscape.bind(this.view), this);
			this.eventManager.on(ScreenEvents.PORTRAIT_MODE, this._view.onPortrait.bind(this.view), this);
			this.eventManager.on(ScreenEvents.SCREEN_RESIZED, this._view.onResized.bind(this.view), this);
			this.eventManager.on(ScreenEvents.FULLSCREEN_ENTERED, this._view.onFullscreenEntered.bind(this.view), this);
			this.eventManager.on(ScreenEvents.FULLSCREEN_EXITED, this._view.onFullscreenExited.bind(this.view), this);
		}
	}

	protected abstract setupViews(): void;

	protected abstract setupStates(): void;

	protected abstract enterFirstState(): void;

	protected changeStateTo(name: string): void {
		const fromState: string = this._currentState ? this._currentState.name : '';
		this._previousStateName = fromState;
		this._currentState && this._currentState.onLeaveState();
		this._currentState = this._states[name];
		this._currentState.onEnterState();

		if (!__PRODUCTION__) {
			Module._debugInfo.setModuleState(this.constructor.name, this._currentState.name);
			console.log('' + this.constructor.name + ': ' + fromState + ' -> ' + this.currentState.name);
		}
	}

	protected setView(viewName: string): void {
		this._view = this._viewManager.getView(viewName) as V;
	}

	protected addState(module: ModuleState<V, Module<V>>): void {
		this._states[module.name] = module;
	}

	protected on(id: string, listener: Function, priority: number = 0): void {
		this.eventManager.on(id, listener.bind(this, id), this, priority);
	}

	protected off(id: string): void {
		this.eventManager.off(id, this);
	}

	protected onCurrentStateHandle(id: string): void {
		if (this._moduleStateHandlingEvents.indexOf(id) < 0) {
			this._moduleStateHandlingEvents.push(id);
			this.on(id, this.onCurrentStateHandleEvent);
		}
	}

	protected removeCurrentStateHandling(id: string): void {
		const index: number = this._moduleStateHandlingEvents.indexOf(id);
		if (index >= 0) {
			this._moduleStateHandlingEvents.splice(index, 1);
			this.off(id);
		}
	}

	protected onView(id: string, listener: Function): void {
		this.view.on(id, listener.bind(this, id), this);
	}

	protected onViewCurrentStateHandle(id: string): void {
		this.onView(id, this.onCurrentStateHandleEvent);
	}

	protected onViewEmitEvent(viewEventID: string, eventToEmitID: string): void {
		this.view.on(
			viewEventID,
			(...args: any[]) => {
				this.eventManager.emit(eventToEmitID, ...args);
			},
			this,
		);
	}

	private onCurrentStateHandleEvent(eventID: string, ...args: any[]): void {
		this.currentState.handle(eventID, ...args);
	}

	public get eventManager(): IEventManagerWithAsync {
		return this._eventManager;
	}

	public get currentState(): ModuleState<V, Module<V>> {
		return this._currentState;
	}

	public get settings(): ISettings {
		return this._settings;
	}

	public get view(): V {
		return this._view;
	}

	public get system(): System {
		return this._system;
	}

	public get updateManager(): UpdateManager {
		return this._updateManager;
	}

	public get logic(): Logic {
		return this._logic;
	}

	public get soundPlayer(): SoundPlayer {
		return this._soundPlayer;
	}

	public get device(): Device {
		return this._device;
	}

	public get timeUtils(): TimerUtils {
		return this._timeUtils;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/common/Module.ts